import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { Panel, PageHeader, Loading, ErrorBox, apiError } from "@/admin/ui";
import LessonsEditor from "@/admin/LessonsEditor";
import ModuleMedia from "@/admin/ModuleMedia";

const EMPTY = { title: "", summary: "", duration: "", number: 1, active: false, free: false, sections: [], clarity: { insight: "", prompt: "" }, next_step: "", media: [] };
const toForm = (m) => ({ ...EMPTY, ...m, sections: (m.sections || []).map((s) => ({ title: s.title, text: s.body.join("\n") })) });

const Field = ({ label, help, children }) => (
  <div className="adm-field"><label className="adm-label">{label}</label>{children}{help && <div className="adm-help">{help}</div>}</div>
);

export default function ModuleEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const isNew = id === "nuevo";
  const [f, setF] = useState(null);
  const [error, setError] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    let live = true;
    setF(null);
    const req = isNew ? api.get("/admin/modules").then((r) => ({ ...toForm(EMPTY), number: r.data.length + 1 }))
      : api.get(`/admin/modules/${id}`).then((r) => toForm(r.data));
    req.then((v) => live && setF(v)).catch(() => live && setError(true));
    return () => { live = false; };
  }, [id, isNew]);
  if (error) return <ErrorBox />;
  if (!f) return <Loading />;
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });

  const save = async () => {
    setSaving(true);
    const body = { ...f, number: Number(f.number) || 1,
      sections: f.sections.filter((s) => s.title.trim()).map((s) => ({ title: s.title.trim(), body: s.text.split("\n").map((l) => l.trim()).filter(Boolean) })) };
    delete body.text;
    try {
      const { data } = isNew ? await api.post("/admin/modules", body) : await api.put(`/admin/modules/${id}`, body);
      toast.success("Cambios guardados correctamente.");
      if (isNew) nav(`/admin/contenido/${data.id}`, { replace: true }); else setF(toForm(data));
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <div data-testid="admin-module-editor">
      <PageHeader title={isNew ? "Nuevo módulo" : "Editar módulo"} subtitle={f.title || "Sin título"}>
        <button className="adm-btn" onClick={() => nav("/admin/contenido")} data-testid="admin-module-cancel">Volver</button>
        <button className="adm-btn primary" onClick={save} disabled={saving || !f.title.trim()} data-testid="admin-module-save">{saving ? "Guardando..." : "Guardar cambios"}</button>
      </PageHeader>
      <div className="adm-grid adm-two">
        <div className="adm-grid">
          <Panel title="Datos del módulo">
            <Field label="Título"><input className="adm-input" value={f.title} onChange={set("title")} maxLength={160} data-testid="admin-module-title" /></Field>
            <Field label="Descripción"><textarea className="adm-textarea" style={{ minHeight: 80 }} value={f.summary} onChange={set("summary")} maxLength={600} data-testid="admin-module-summary" /></Field>
            <div className="adm-form-grid">
              <Field label="Orden" help="Posición en la Academia. El 1 es el módulo de bienvenida."><input type="number" min={1} className="adm-input" value={f.number} onChange={set("number")} data-testid="admin-module-number" /></Field>
              <Field label="Duración" help="Ej. 8 min"><input className="adm-input" value={f.duration} onChange={set("duration")} maxLength={40} data-testid="admin-module-duration" /></Field>
            </div>
          </Panel>
          <LessonsEditor sections={f.sections} onChange={(sections) => setF({ ...f, sections })} />
          <Panel title="Cierre del módulo">
            <Field label="Idea clave"><textarea className="adm-textarea" style={{ minHeight: 70 }} value={f.clarity.insight} onChange={(e) => setF({ ...f, clarity: { ...f.clarity, insight: e.target.value } })} data-testid="admin-module-insight" /></Field>
            <Field label="Pregunta para el diario"><input className="adm-input" value={f.clarity.prompt} onChange={(e) => setF({ ...f, clarity: { ...f.clarity, prompt: e.target.value } })} data-testid="admin-module-prompt" /></Field>
            <Field label="Siguiente paso"><input className="adm-input" value={f.next_step} onChange={set("next_step")} data-testid="admin-module-next-step" /></Field>
          </Panel>
        </div>
        <div className="adm-grid" style={{ alignContent: "start" }}>
          <Panel title="Publicación">
            <div className="adm-list-item"><span><b>Activo</b><div className="adm-sub">Visible para los alumnos</div></span>
              <Switch checked={f.active} onCheckedChange={set("active")} data-testid="admin-module-active" /></div>
            <div className="adm-list-item"><span><b>Acceso</b><div className="adm-sub">General: cuentas gratuitas · Premium: planes de pago</div></span>
              <Select value={f.free ? "general" : "premium"} onValueChange={(v) => setF({ ...f, free: v === "general" })}>
                <SelectTrigger className="w-[130px] bg-white" data-testid="admin-module-access"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="general">General</SelectItem><SelectItem value="premium">Premium</SelectItem></SelectContent>
              </Select></div>
          </Panel>
          <ModuleMedia media={f.media} onChange={(media) => setF({ ...f, media })} />
        </div>
      </div>
    </div>
  );
}
